import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TestModuleDataSheetColumnMapping, TestModuleParameter } from '../../../../models/test-module.model';
import { Testdatenblatt } from '../../../../models/testdatenblatt.model';
import { SecretColumnError } from '../../../../shared/http/server-message';

@Component({
  selector: 'app-test-module-column-mappings-panel',
  imports: [TranslatePipe],
  templateUrl: './test-module-column-mappings-panel.component.html',
  styleUrl: './test-module-column-mappings-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestModuleColumnMappingsPanelComponent {
  readonly sheet = input.required<Testdatenblatt>();
  readonly parameters = input<TestModuleParameter[]>([]);
  readonly mappings = input<TestModuleDataSheetColumnMapping[]>([]);
  readonly readOnly = input(false);
  readonly secretError = input<Pick<SecretColumnError, 'field' | 'column'> | null>(null);

  readonly mappingsChange = output<TestModuleDataSheetColumnMapping[]>();
  readonly changed = output<void>();

  readonly parameterNames = computed(() => this.parameters().map((p) => p.name));

  private mappingsByColumn = computed(() => {
    const map = new Map<string, TestModuleDataSheetColumnMapping>();
    for (const m of this.mappings()) {
      if (m.testdatenblattId === this.sheet().id) {
        map.set(m.sheetColumn, m);
      }
    }
    return map;
  });

  readonly orphanMappings = computed(() => {
    const { id, spalten } = this.sheet();
    return this.mappings().filter(
      (m) => m.testdatenblattId === id && m.parameterName != null && !spalten.includes(m.sheetColumn),
    );
  });

  readonly missingRequired = computed(() => {
    const required = this.parameters().filter((p) => p.required);
    const covered = new Set<string>();
    for (const col of this.sheet().spalten) {
      const effective = this.effectiveParameter(col);
      if (effective) covered.add(effective);
    }
    return required.filter((p) => !covered.has(p.name)).map((p) => p.name);
  });

  effectiveParameter(column: string): string | null {
    const explicit = this.mappingsByColumn().get(column);
    if (explicit) return explicit.parameterName;
    return this.parameterNames().includes(column) ? column : null;
  }

  selectValue(column: string): string {
    const value = this.effectiveParameter(column);
    return value ?? '';
  }

  isRequired(name: string): boolean {
    return this.parameters().some((p) => p.name === name && p.required);
  }

  onSelectChange(column: string, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.setMapping(column, value);
  }

  setMapping(column: string, parameterName: string): void {
    const sheetId = this.sheet().id;
    const next: TestModuleDataSheetColumnMapping[] = [];
    let replaced = false;
    for (const m of this.mappings()) {
      if (m.testdatenblattId === sheetId && m.sheetColumn === column) {
        next.push({ ...m, parameterName: parameterName === '' ? null : parameterName });
        replaced = true;
      } else {
        next.push(m);
      }
    }
    if (!replaced) {
      next.push({
        testdatenblattId: sheetId,
        sheetColumn: column,
        parameterName: parameterName === '' ? null : parameterName,
      });
    }
    this.mappingsChange.emit(next);
    this.changed.emit();
  }

  trackColumn(_index: number, column: string): string {
    return column;
  }
}
